import { classNames } from '@geerd-nx/utils';
import { Menu, Transition } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/20/solid';
import { PrinterIcon } from '@heroicons/react/24/solid';
import printElement from 'libs/utils/src/PrintElement';
import { Fragment, useState } from 'react';
import { ToolbarProps, View } from 'react-big-calendar';
import {
  addMonths,
  startOfMonth,
  isBefore,
  endOfMonth,
  format,
  isAfter,
} from 'date-fns';

export const Toolbar: React.ComponentType<
  ToolbarProps & {
    addEvent?: { label: string; onClick: () => void };
    onMonthChange?: (startDate: string, endDate: string) => void;
    calendarId: string;
  }
> = ({
  date,
  label,
  onNavigate,
  onView,
  view,
  views,
  addEvent,
  localizer,
  onMonthChange,
  calendarId,
}) => {
  const [fetchedDatesExtremums, setFetchedDatesExtremums] = useState({
    least: startOfMonth(addMonths(date, -1)),
    greatest: startOfMonth(addMonths(date, 1)),
  });
  const viewsArray =
    'length' in views
      ? views
      : (Object.entries(views)
          .filter((entry) => entry[1])
          .map((entry) => entry[0]) as View[]);

  return (
    <>
      <header className="flex bg-gray-100 items-center justify-between border-b border-gray-200 py-4 px-6 lg:flex-none print:hidden">
        <h3 className="text-base font-semibold leading-6 text-gray-900 capitalize">
          <time dateTime={label}>{label}</time>
        </h3>
        <div className="flex items-center">
          <div className="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch">
            <div
              className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-inset ring-gray-300"
              aria-hidden="true"
            />
            <button
              onClick={() => {
                onNavigate('PREV');
                const start = startOfMonth(addMonths(date, -2));

                if (
                  onMonthChange &&
                  isBefore(start, fetchedDatesExtremums.least)
                ) {
                  const end = format(
                    endOfMonth(addMonths(date, -2)),
                    'yyyy-MM-dd HH:mm'
                  );

                  onMonthChange(format(start, 'yyyy-MM-dd HH:mm'), end);
                  setFetchedDatesExtremums({
                    ...fetchedDatesExtremums,
                    least: start,
                  });
                }
              }}
              type="button"
              className="flex items-center justify-center rounded-l-md py-2 pl-3 pr-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
            >
              <span className="sr-only">{localizer.messages.previous}</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => onNavigate('TODAY')}
              type="button"
              className="hidden px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
            >
              {localizer.messages.today}
            </button>
            <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
            <button
              onClick={() => {
                onNavigate('NEXT');
                const start = startOfMonth(addMonths(date, 2));
                if (
                  onMonthChange &&
                  isAfter(start, fetchedDatesExtremums.greatest)
                ) {
                  const end = format(
                    endOfMonth(addMonths(date, 2)),
                    'yyyy-MM-dd HH:mm'
                  );
                  onMonthChange(format(start, 'yyyy-MM-dd HH:mm'), end);
                  setFetchedDatesExtremums({
                    ...fetchedDatesExtremums,
                    greatest: start,
                  });
                }
              }}
              type="button"
              className="flex items-center justify-center rounded-r-md py-2 pl-4 pr-3 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
            >
              <span className="sr-only">{localizer.messages.next}</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="hidden md:ml-4 md:flex md:items-center">
            <Menu as="div" className="relative">
              <Menu.Button
                type="button"
                className="flex items-center capitalize gap-x-1.5 rounded-md bg-white py-2 px-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                {localizer.messages[view]}
                <ChevronDownIcon
                  className="-mr-1 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    {viewsArray.map((view) => (
                      <Menu.Item key={view}>
                        {({ active }) => (
                          <button
                            onClick={() => onView(view)}
                            className={classNames(
                              active
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-700',
                              'block px-4 py-2 text-sm',
                              'capitalize w-full'
                            )}
                          >
                            {localizer.messages[view]}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
            {addEvent && (
              <>
                <div className="ml-6 h-6 w-px bg-gray-300" />
                <button
                  onClick={addEvent.onClick}
                  type="button"
                  className="ml-6 rounded-md  bg-primary-600 py-2 px-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  {addEvent.label}
                </button>
              </>
            )}
          </div>
          <div className="ml-4">
            <button
              className="flex items-center capitalize gap-x-1.5 rounded-md bg-white py-2 px-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              type="button"
              onClick={() => {
                printElement(calendarId, {
                  title: label,
                  size: 'landscape',
                  mmMargin: 4,
                });
              }}
            >
              <div className="sr-only">Print Calendar</div>
              <PrinterIcon className="w-5 h-5" />
            </button>
          </div>
          <Menu as="div" className="relative ml-6 md:hidden">
            <Menu.Button className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
              <span className="sr-only">Open menu</span>
              <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {addEvent && (
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={classNames(
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700',
                            'block px-4 py-2 text-sm w-full'
                          )}
                        >
                          {addEvent.label}
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                )}
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => onNavigate('TODAY')}
                        className={classNames(
                          active
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700',
                          'block px-4 py-2 text-sm w-full'
                        )}
                      >
                        {localizer.messages.today}
                      </button>
                    )}
                  </Menu.Item>
                </div>
                <div className="py-1 ">
                  {viewsArray.map((view) => (
                    <Menu.Item key={view}>
                      {({ active }) => (
                        <button
                          onClick={() => onView(view)}
                          className={classNames(
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700',
                            'block px-4 py-2 text-sm w-full'
                          )}
                        >
                          {localizer.messages[view]}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </header>
      {/* PRINT HEADER */}
      <div className="py-4 px-6 items-center justify-between hidden print:flex">
        <h1 className="text-lg font-semibold leading-6 text-gray-900 capitalize">
          <time dateTime={label}>{label}</time>
        </h1>
      </div>
    </>
  );
};
